import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    ALGEBRA_VARIABLES,
    AlgebraQuestionOptions,
    Operations,
    ProblemData,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import {
    EXPRESSION_SHAPES,
    ExpressionShape,
    expressionPhrases as en,
} from "../Files/Prompts/en/expressionPhrases.ts";
import { expressionPhrases as es } from "../Files/Prompts/es/expressionPhrases.ts";
import { expressionPhrases as fr } from "../Files/Prompts/fr/expressionPhrases.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Algebra problem generation. See ADR 0028.
 *
 * Everything here is built answer-first: the solution is drawn, then the
 * problem is constructed around it. That is what makes the constraint in
 * ADR 0028 §1 - whole numbers throughout, and exact division - hold by
 * construction rather than by filtering.
 *
 * Non-negativity is now **the default rather than the rule** (ADR 0036 §2).
 * `AllowNegatives` turns it off, and is off unless someone deliberately turns
 * it on, because a sixth grader solving `x + 7 = 12` has not met negative
 * numbers. Exact division does not relax with it: that half of the constraint
 * has nothing to do with sign.
 *
 * ADR 0019 §3 generates an expression and rejects it if it breaks the rule.
 * Nothing here needs that, and the difference is worth stating: an
 * order-of-operations problem has a property (correct evaluation differs from
 * left-to-right) that cannot be built in, only tested for. An algebra problem
 * has no such property, so there is no draw to reject - a subtraction is built
 * from a minuend that is already large enough, and a division from a dividend
 * that is already a multiple. There is deliberately no null path here; an
 * unreachable one would be untested branch cover pretending to be a guard.
 *
 * The operations an equation can use come from the visitor's operator
 * selection, so a multiplication-only sheet is all `6a = 42` and never
 * `x + 7 = 12` (ADR 0028 §4).
 *
 * The expression types (distributive property, like terms, factoring; ADR
 * 0119) answer with an expression, not a number. They ignore the operator bar,
 * write every answer in one fixed form (`linear`), and read the dial
 * differently: it caps every number printed, prompt and answer, since there is
 * no hidden solution for it to cap.
 *
 * Writing Expressions (ADR 0123) is the one type with words in it, and so the
 * one that reads the printed language. It is built from a fixed bank of
 * phrasings per shape (`Files/Prompts/{en,es,fr}/expressionPhrases.ts`), and the key is
 * the shape's own form whatever the phrasing. Every other type is passed
 * nothing of the language and prints exactly as before; a golden test holds it.
 * Systems of Equations (ADR 0139) reads it too, but only for the separator in
 * the pair that answers it: `(3, 4)`, and `(3 ; 4)` in French.
 */

/** The three single-skill expression types Equivalent deals between (ADR 0119 §1). */
const EXPRESSION_TYPES = [
    AlgebraQuestionOptions.Distribute,
    AlgebraQuestionOptions.CombineLikeTerms,
    AlgebraQuestionOptions.Factor,
];

/**
 * Which question types a setting asks for. Mixed means the four that predate
 * ADR 0119; the expression types are deliberately not in it, so every existing
 * sheet prints exactly as it did.
 */
function questionTypes(question: AlgebraQuestionOptions): AlgebraQuestionOptions[] {
    if (question === AlgebraQuestionOptions.Equivalent) {
        return EXPRESSION_TYPES;
    }
    if (question === AlgebraQuestionOptions.Mixed) {
        // Mixed now spans sixth grade to seventh, which ADR 0036
        // §Consequences flags as a real question it does not settle.
        return [
            AlgebraQuestionOptions.SolveOneStep,
            AlgebraQuestionOptions.SolveTwoStep,
            AlgebraQuestionOptions.Inequality,
            AlgebraQuestionOptions.EvaluateExpression,
        ];
    }
    return [question];
}

type AlgebraOp = "+" | "-" | "x" | "÷";

/**
 * The operations available on this sheet.
 *
 * Long division is the same arithmetic as division here - there is no bracket
 * layout for an equation - so it maps onto `÷`. A selection with nothing usable
 * falls back to addition rather than returning nothing, which is the precedent
 * the money generator set for an operator selection it cannot serve.
 */
function operatorsFor(settings: ISettingsProps): AlgebraOp[] {
    const selected = settings.Operators?.map((operator) => operator.value) ?? [];
    const available: AlgebraOp[] = [];

    if (selected.includes(Operations.Addition)) available.push("+");
    if (selected.includes(Operations.Subtraction)) available.push("-");
    if (selected.includes(Operations.Multiplication)) available.push("x");
    if (selected.includes(Operations.Division) || selected.includes(Operations.LongDivision)) {
        available.push("÷");
    }

    return available.length > 0 ? available : ["+"];
}

/**
 * The largest value a problem may be built from.
 *
 * Clamped because several draws below need a range of at least 2, and a
 * setting arriving smaller than that would otherwise reach getRandomInt with
 * its arguments the wrong way round.
 */
function usableMax(maxValue: number): number {
    return Math.max(2, Math.floor(maxValue) || 2);
}

/**
 * A value that may be negative, when the sheet allows it.
 *
 * Zero is excluded from the negative case so a "negative" draw is actually
 * negative - `-0` is 0 and would make the setting look inert.
 */
function maybeSigned(magnitude: number, allowNegatives: boolean): number {
    if (!allowNegatives || getRandomInt(0, 1) === 0) {
        return magnitude;
    }
    return -magnitude;
}

function randomVariable(): string {
    return ALGEBRA_VARIABLES[getRandomInt(0, ALGEBRA_VARIABLES.length - 1)];
}

/** Every whole number from 2 up to `max` that divides `value` exactly. */
function divisorsOf(value: number, max: number): number[] {
    const divisors: number[] = [];
    for (let candidate = 2; candidate <= Math.min(max, value); candidate++) {
        if (value % candidate === 0) {
            divisors.push(candidate);
        }
    }
    return divisors;
}

/**
 * One equation with a single unknown.
 *
 * The solution is drawn first and the equation built to match, so the answer is
 * known before the problem exists and no arm can produce a negative value or an
 * inexact division.
 */
function buildSolveProblem(
    operator: AlgebraOp,
    maxValue: number,
    variable: string,
    allowNegatives: boolean
): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    const solution = maybeSigned(getRandomInt(1, max), allowNegatives);

    switch (operator) {
        case "+": {
            // x + b = solution + b. With negatives off both sides are sums of
            // values inside the range, so nothing here can go negative.
            const b = getRandomInt(1, max);
            return {
                prompt: `${variable} + ${b} = ${solution + b}`,
                answer: `${variable} = ${solution}`,
            };
        }
        case "-": {
            // x - b = solution, so x is the larger side by construction and the
            // difference is the answer that was drawn.
            const b = getRandomInt(1, max);
            return {
                prompt: `${variable} - ${b} = ${solution}`,
                answer: `${variable} = ${solution + b}`,
            };
        }
        case "x": {
            // Juxtaposition, which is part of the skill (ADR 0028 §5).
            const coefficient = getRandomInt(2, Math.min(12, max));
            return {
                prompt: `${coefficient}${variable} = ${solution * coefficient}`,
                answer: `${variable} = ${solution}`,
            };
        }
        default: {
            // x ÷ b = solution, so the dividend is a multiple of b by
            // construction and the division is exact.
            const b = getRandomInt(2, Math.min(12, max));
            return {
                prompt: `${variable} ÷ ${b} = ${solution}`,
                answer: `${variable} = ${solution * b}`,
            };
        }
    }
}

/**
 * A two-step equation: `3c + 4 = 25`, or `c ÷ 3 - 2 = 4`.
 *
 * Answer-first like the one-step arms, so the right-hand side is whatever the
 * chosen solution makes it. With negatives off, the constant is drawn from
 * inside the term so the right-hand side stays positive; with them on it need
 * not be.
 */
function buildTwoStepProblem(
    maxValue: number,
    variable: string,
    allowNegatives: boolean
): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    // From two, not one: with a magnitude of one the constant below has only
    // itself to draw, and `x ÷ 11 - 1 = 0` is the result.
    const solution = maybeSigned(getRandomInt(2, max), allowNegatives);
    const adding = getRandomInt(0, 1) === 0;
    const sign = adding ? "+" : "-";

    if (getRandomInt(0, 1) === 0) {
        const coefficient = getRandomInt(2, Math.min(12, max));
        const term = coefficient * solution;
        // Kept inside the term when negatives are off, so `3c - 20 = -5` only
        // appears on a sheet that asked for it.
        // Kept below the term rather than up to it: drawing the constant
        // equal to the term makes `12c - 72 = 0`, and a sheet came back with
        // four right-hand sides of zero out of eighteen.
        const constant = allowNegatives
            ? getRandomInt(1, max)
            : getRandomInt(1, Math.max(1, Math.abs(term) - 1));
        return {
            prompt: `${coefficient}${variable} ${sign} ${constant} = ${adding ? term + constant : term - constant}`,
            answer: `${variable} = ${solution}`,
        };
    }

    // Divide-then-add. The dividend is a multiple of the divisor by
    // construction, so the division is exact whatever the signs are.
    const divisor = getRandomInt(2, Math.min(12, max));
    const quotient = maybeSigned(getRandomInt(2, max), allowNegatives);
    const constant = allowNegatives
        ? getRandomInt(1, max)
        : getRandomInt(1, Math.max(1, Math.abs(quotient) - 1));
    return {
        prompt: `${variable} ÷ ${divisor} ${sign} ${constant} = ${adding ? quotient + constant : quotient - constant}`,
        answer: `${variable} = ${quotient * divisor}`,
    };
}

/** One of the entries of `list`, uniformly. */
function pick<T>(list: T[]): T {
    return list[getRandomInt(0, list.length - 1)];
}

/**
 * The constants `b` that keep `d = shift + b` valid (ADR 0138 §2): both
 * non-zero, both within `bound`. `shift` is `(a - c)x`, what the coefficients
 * and the solution leave between the two constants.
 */
function constantsFitting(shift: number, bound: number): number[] {
    const fitting: number[] = [];
    for (let b = -bound; b <= bound; b++) {
        const d = shift + b;
        if (b !== 0 && d !== 0 && Math.abs(d) <= bound) fitting.push(b);
    }
    return fitting;
}

/**
 * An equation with the variable on both sides: `5x - 3 = 2x + 9`
 * (ADR 0138). Always four terms, `ax + b = cx + d`, and always one solution.
 *
 * Drawn in order, not drawn and rejected: the solution, then a coefficient
 * pair among those that leave at least one valid constant (few pairs fit a
 * large solution, so rejecting would push large solutions out), then the first
 * constant among those that keep the second in range. `d = (a - c)x + b`, so
 * the key is the drawn solution.
 *
 * Negatives off means no negative on the way, not just in the prompt: the
 * smaller coefficient's side is `cx + d` with `d` at least 1, and collecting
 * onto the larger one leaves `(a - c)x = d - b`, positive by construction.
 * That side is then swapped to the right half the time; the equation is the
 * same either way, so is the answer.
 */
function buildBothSidesProblem(
    maxValue: number,
    variable: string,
    allowNegatives: boolean
): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    // Never three digits: at dial 50 a constant of 100 is the only one that overflows its half
    // of the row (`4m - 100 = -4m + 100`), so the bound is twice the dial, up to 99.
    const bound = Math.min(2 * max, 99);
    const solution = maybeSigned(getRandomInt(2, max), allowNegatives);
    let left: [number, number];
    let right: [number, number];

    if (allowNegatives) {
        // Each coefficient is negative on a coin of its own; the magnitudes
        // are then drawn from the pairs that can be finished.
        const leftSign = getRandomInt(0, 1) === 0 ? 1 : -1;
        const rightSign = getRandomInt(0, 1) === 0 ? 1 : -1;
        const pairs: Array<[number, number]> = [];
        for (let first = 1; first <= 9; first++) {
            for (let second = 1; second <= 9; second++) {
                const a = leftSign * first;
                const c = rightSign * second;
                if (a !== c && constantsFitting((a - c) * solution, bound).length > 0) {
                    pairs.push([a, c]);
                }
            }
        }
        const [a, c] = pick(pairs);
        const b = pick(constantsFitting((a - c) * solution, bound));
        left = [a, b];
        right = [c, (a - c) * solution + b];
    } else {
        // Larger coefficient `big`, smaller `small`, solution positive.
        const pairs: Array<[number, number]> = [];
        for (let big = 2; big <= 9; big++) {
            for (let small = 1; small < big; small++) {
                const shift = (big - small) * solution;
                if (smallSideConstants(shift, bound).length > 0) {
                    pairs.push([big, small]);
                }
            }
        }
        const [big, small] = pick(pairs);
        const d = pick(smallSideConstants((big - small) * solution, bound));
        const larger: [number, number] = [big, d - (big - small) * solution];
        const smaller: [number, number] = [small, d];
        const largerOnRight = getRandomInt(0, 1) === 0;
        left = largerOnRight ? smaller : larger;
        right = largerOnRight ? larger : smaller;
    }

    return {
        prompt: `${linear(left[0], left[1], variable)} = ${linear(right[0], right[1], variable)}`,
        answer: `${variable} = ${solution}`,
    };
}

/**
 * The positive constants `d` for the smaller-coefficient side when negatives
 * are off: 1 to `bound`, leaving the larger side's `d - shift` non-zero and
 * within `bound`.
 */
function smallSideConstants(shift: number, bound: number): number[] {
    const fitting: number[] = [];
    for (let d = 1; d <= bound; d++) {
        const b = d - shift;
        if (b !== 0 && Math.abs(b) <= bound) fitting.push(d);
    }
    return fitting;
}

/** `>` becomes `<` when both sides are divided by a negative. */
function flipped(relation: string): string {
    return relation === ">" ? "<" : ">";
}

/**
 * A one-variable inequality: `2x + 3 > 11`, answering `x > 4`.
 *
 * The flip is the point (ADR 0036 §4). A negative coefficient reverses the
 * relation - `-2x > 8` gives `x < -4` - and it is the one rule in this format
 * a student gets wrong. It is only reachable with negatives on, which keeps
 * the sixth-grade sheet free of it.
 */
function buildInequalityProblem(
    maxValue: number,
    variable: string,
    allowNegatives: boolean
): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    const bound = maybeSigned(getRandomInt(1, max), allowNegatives);
    const coefficient = maybeSigned(getRandomInt(2, Math.min(12, max)), allowNegatives);
    const constant = getRandomInt(1, max);
    const relation = getRandomInt(0, 1) === 0 ? ">" : "<";
    const rightHandSide = coefficient * bound + constant;

    return {
        prompt: `${coefficient}${variable} + ${constant} ${relation} ${rightHandSide}`,
        answer: `${variable} ${coefficient < 0 ? flipped(relation) : relation} ${bound}`,
    };
}

/**
 * One expression to evaluate at a given value.
 *
 * The subtraction arm draws its constant from below the term, and the division
 * arm from the term's own divisors, so both are valid by construction.
 *
 * The value the variable takes scales with the setting; the coefficient does
 * not. A coefficient is a small multiplier in 6.EE - `3n + 2`, not `47n + 2` -
 * so the difficulty dial moves the number being substituted, which is the part
 * a student actually works with. The multiplication arm is the exception: it
 * multiplies the term again, so a value drawn from the full range would make a
 * three-factor product in the thousands. It keeps the tighter bound, and the
 * setting still moves it through the coefficient and the multiplier.
 */
function buildEvaluateProblem(
    operator: AlgebraOp,
    maxValue: number,
    variable: string
): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    const coefficient = getRandomInt(2, Math.min(9, max));

    if (operator === "x") {
        const value = getRandomInt(1, Math.min(12, max));
        const multiplier = getRandomInt(2, Math.min(9, max));
        return {
            prompt: `${variable} = ${value},   ${coefficient}${variable} × ${multiplier}`,
            answer: String(coefficient * value * multiplier),
        };
    }

    const value = getRandomInt(1, max);
    const term = coefficient * value;

    switch (operator) {
        case "-": {
            // Drawn from within the term, so the result cannot go below zero.
            const constant = getRandomInt(1, term);
            return {
                prompt: `${variable} = ${value},   ${coefficient}${variable} - ${constant}`,
                answer: String(term - constant),
            };
        }
        case "÷": {
            // The coefficient always divides the term exactly, so this list is
            // never empty and the division is always clean.
            const divisors = divisorsOf(term, Math.min(12, max));
            const divisor = divisors[getRandomInt(0, divisors.length - 1)];
            return {
                prompt: `${variable} = ${value},   ${coefficient}${variable} ÷ ${divisor}`,
                answer: String(term / divisor),
            };
        }
        default: {
            const constant = getRandomInt(1, max);
            return {
                prompt: `${variable} = ${value},   ${coefficient}${variable} + ${constant}`,
                answer: String(term + constant),
            };
        }
    }
}

/**
 * `ax + b` in the one fixed form the key uses (ADR 0119 §2): the variable term
 * first, a coefficient of 1 not written, a negative constant as a spaced
 * subtraction (never `+ -`), and no zero term. A leading negative carries its
 * sign against the term, `-6x + 15`. The prompt's terms are written by this
 * too, so `3(x + 4)` never prints as `3(1x + 4)`.
 */
function linear(coefficient: number, constant: number, variable: string): string {
    return `${leadingTerm(coefficient, variable)} ${constant < 0 ? "-" : "+"} ${Math.abs(constant)}`;
}

/** `3x`, `x`, `-x`: a variable term at the start of an expression, a 1 not written. */
function leadingTerm(coefficient: number, variable: string): string {
    const magnitude = Math.abs(coefficient);
    return `${coefficient < 0 ? "-" : ""}${magnitude === 1 ? "" : magnitude}${variable}`;
}

function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
}

/**
 * `3(2x + 5)` -> `6x + 15`.
 *
 * Every number printed, prompt and answer, is inside the dial (ADR 0119 §4), so
 * the inner values are drawn from `max / g`: `g·a` and `g·b` are what the key
 * prints. With negatives on the multiplier and the inner leading coefficient
 * each go negative half the time, and the key follows (`-3(2x - 5)` ->
 * `-6x + 15`).
 */
function buildDistributeProblem(
    maxValue: number,
    variable: string,
    allowNegatives: boolean
): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    const magnitude = getRandomInt(2, Math.min(9, max));
    const inside = Math.floor(max / magnitude);
    const multiplier = maybeSigned(magnitude, allowNegatives);
    const coefficient = maybeSigned(getRandomInt(1, Math.min(9, inside)), allowNegatives);
    const constant = getRandomInt(1, inside) * (getRandomInt(0, 1) === 0 ? 1 : -1);

    return {
        prompt: `${multiplier}(${linear(coefficient, constant, variable)})`,
        answer: linear(multiplier * coefficient, multiplier * constant, variable),
    };
}

/**
 * Two non-zero parts of `total` (positive), each inside the dial.
 *
 * Mostly an honest split, `4x` into `3x` and `x`. When the total is 1, or on
 * one draw in three, one part is a subtraction instead (`5x - x`), which is
 * what makes like terms worth hunting for. Always possible for a max of 2 or
 * more: a split needs the total to be 2 or more, and a cancelling pair needs
 * room above the total, and the first fails only at 1 where the second holds.
 */
function splitPositive(total: number, max: number): [number, number] {
    const room = Math.min(9, max - total);
    if ((total < 2 || getRandomInt(0, 2) === 0) && room >= 1) {
        const taken = getRandomInt(1, room);
        return [total + taken, -taken];
    }
    const first = getRandomInt(1, total - 1);
    return [first, total - first];
}

/** `splitPositive` for a total of either sign. */
function splitParts(total: number, max: number): [number, number] {
    const [first, second] = splitPositive(Math.abs(total), max);
    return total < 0 ? [-first, -second] : [first, second];
}

/** The term `3x` or `7` as a signed value, for printing in a run. */
interface Term {
    value: number;
    withVariable: boolean;
}

/**
 * `4x + 7 - x + 2` -> `3x + 9`.
 *
 * The answer is drawn first, then each half is split in two, so there are four
 * terms - two in x, two constants - in shuffled order. Like terms are not
 * always side by side. A subtracted term is a spaced subtraction rather than a
 * negative number, so it is allowed with negatives off; what is not allowed
 * there is a sheet that starts with one.
 */
function buildCombineProblem(
    maxValue: number,
    variable: string,
    allowNegatives: boolean
): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    const coefficient = maybeSigned(getRandomInt(1, max), allowNegatives);
    const constant = getRandomInt(1, max) * (getRandomInt(0, 1) === 0 ? 1 : -1);

    const terms: Term[] = [
        ...splitParts(coefficient, max).map((value) => ({ value, withVariable: true })),
        ...splitParts(constant, max).map((value) => ({ value, withVariable: false })),
    ];
    const shuffled = shuffle(terms);
    if (!allowNegatives && shuffled[0].value < 0) {
        // A positive term always exists: the x parts sum to at least 1.
        const positive = shuffled.findIndex((term) => term.value > 0);
        [shuffled[0], shuffled[positive]] = [shuffled[positive], shuffled[0]];
    }

    const prompt = shuffled
        .map((term, index) => {
            const size = Math.abs(term.value);
            const text = term.withVariable && size === 1 ? variable : `${size}${term.withVariable ? variable : ""}`;
            if (index === 0) {
                return `${term.value < 0 ? "-" : ""}${text}`;
            }
            return `${term.value < 0 ? "-" : "+"} ${text}`;
        })
        .join(" ");

    return { prompt, answer: linear(coefficient, constant, variable) };
}

/**
 * `12x + 18` -> `6(2x + 3)`.
 *
 * `gcd(a, b) = 1` is what makes `g` the greatest common factor, and the leading
 * term is always positive so the key has one form (ADR 0119 §3).
 *
 * Drawn uniformly over every (g, a, b) the dial allows, not g first. Above
 * half the dial only `g(x ± 1)` fits, so drawing g first made about half of a
 * dial-12 sheet `9c + 9`, `7n + 7`, `8k - 8`; drawn over the whole set those
 * are about one in six, and every factor from 2 to 9 can still appear.
 */
function buildFactorProblem(maxValue: number, variable: string): { prompt: string; answer: string } {
    const max = usableMax(maxValue);
    const candidates: Array<[number, number, number]> = [];
    for (let g = 2; g <= Math.min(9, max); g++) {
        const inside = Math.floor(max / g);
        for (let a = 1; a <= inside; a++) {
            for (let b = 1; b <= inside; b++) {
                if (gcd(a, b) === 1) candidates.push([g, a, b]);
            }
        }
    }
    // g = 2, a = b = 1 always fits, since max is at least 2.
    const [factor, coefficient, size] = candidates[getRandomInt(0, candidates.length - 1)];
    const constant = getRandomInt(0, 1) === 0 ? size : -size;

    return {
        prompt: linear(factor * coefficient, factor * constant, variable),
        answer: `${factor}(${linear(coefficient, constant, variable)})`,
    };
}

/** The three kinds of system a sheet is dealt over (ADR 0139 §2). */
export type SystemKind = NonNullable<ProblemData["system"]>["kind"];
export const SYSTEM_KINDS: SystemKind[] = ["substitution", "elimination", "multiplyFirst"];

/** One equation as numbers, `ax + by = c`, for the checks that decide what a kind allows. */
interface Row {
    a: number;
    b: number;
    c: number;
}

type Equations = [[string, string], [string, string]];

/** Zero means the two lines are parallel or the same, so no single solution. */
function determinant(first: Row, second: Row): number {
    return first.a * second.b - first.b * second.a;
}

function coin(): boolean {
    return getRandomInt(0, 1) === 0;
}

/**
 * Every `ax + by = c` the solution (x, y) satisfies with coefficients of size 1
 * to 5 and a constant that is not 0 and fits `bound` (ADR 0139 §3). With
 * negatives off the coefficients are positive too, and with a positive solution
 * so is the constant.
 */
function systemRows(x: number, y: number, bound: number, allowNegatives: boolean): Row[] {
    const sizes = allowNegatives ? [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5];
    const rows: Row[] = [];
    for (const a of sizes) {
        for (const b of sizes) {
            const c = a * x + b * y;
            if (c !== 0 && Math.abs(c) <= bound) rows.push({ a, b, c });
        }
    }
    return rows;
}

/** A row as the two sides it prints: `2x + 3y` and `18`, `-x - 5y` and `-7`. */
function rowSides({ a, b, c }: Row): [string, string] {
    return [`${leadingTerm(a, "x")} ${b < 0 ? "-" : "+"} ${leadingTerm(Math.abs(b), "y")}`, String(c)];
}

/**
 * A first row, and a partner for it that the kind allows. The first row is
 * uniform over those that have a partner, found by dropping the ones that
 * don't as they come up, so no row is passed over for a reason the draw hides.
 * `null` when no row has one: the caller draws another solution.
 */
function drawPair(rows: Row[], partnersOf: (first: Row) => Row[]): [Row, Row] | null {
    const candidates = [...rows];
    while (candidates.length > 0) {
        const index = getRandomInt(0, candidates.length - 1);
        const partners = partnersOf(candidates[index]);
        if (partners.length > 0) return [candidates[index], pick(partners)];
        candidates.splice(index, 1);
    }
    return null;
}

/** The pair in the order it prints: swapped on a coin, unless the kind has fixed it. */
function printed(pair: [Row, Row]): Equations {
    const ordered: [Row, Row] = coin() ? pair : [pair[1], pair[0]];
    return [rowSides(ordered[0]), rowSides(ordered[1])];
}

/**
 * `y = 2x + 1` with `3x + y = 16` (ADR 0139 §2). The solved equation is for `y`,
 * or for `x` when only that fits the constant bound; a coin decides when both
 * do. With negatives off the slope and the constant are both at least 1, so
 * substituting leaves `(a + bm)x = c - bk` positive on both sides (§4).
 */
function substitutionEquations(x: number, y: number, bound: number, allowNegatives: boolean): Equations | null {
    const rows = systemRows(x, y, bound, allowNegatives);
    const slopes = allowNegatives ? [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5];
    const forms = [
        { solved: "y", other: "x", value: y, given: x },
        { solved: "x", other: "y", value: x, given: y },
    ];
    const viable = forms.flatMap((form) => {
        const options = slopes
            .map((m) => ({ m, k: form.value - m * form.given }))
            .filter(({ k }) => k !== 0 && Math.abs(k) <= bound && (allowNegatives || k >= 1))
            .map(({ m, k }) => {
                const row: Row = form.solved === "y" ? { a: -m, b: 1, c: k } : { a: 1, b: -m, c: k };
                return { m, k, partners: rows.filter((other) => determinant(row, other) !== 0) };
            })
            .filter((option) => option.partners.length > 0);
        return options.length > 0 ? [{ form, options }] : [];
    });
    if (viable.length === 0) return null;

    const { form, options } = pick(viable);
    const { m, k, partners } = pick(options);
    const solved: [string, string] = [form.solved, linear(m, k, form.other)];
    const other = rowSides(pick(partners));
    return coin() ? [solved, other] : [other, solved];
}

/**
 * `2x + 3y = 18` with `2x + y = 10` (ADR 0139 §2): one variable has the same
 * coefficient in both, or with negatives on, by a coin, opposite ones. With
 * negatives off the coefficients match, and the equation with the larger
 * remaining coefficient prints first, so first minus second is positive (§4).
 */
function eliminationEquations(x: number, y: number, bound: number, allowNegatives: boolean): Equations | null {
    const onX = coin();
    const opposite = allowNegatives && coin();
    const rows = systemRows(x, y, bound, allowNegatives);
    const pair = drawPair(rows, (first) =>
        rows.filter((second) => {
            const [one, two] = onX ? [first.a, second.a] : [first.b, second.b];
            return two === (opposite ? -one : one) && determinant(first, second) !== 0;
        })
    );
    if (pair === null) return null;
    if (allowNegatives) return printed(pair);

    const remaining = (row: Row) => (onX ? row.b : row.a);
    const [larger, smaller] = remaining(pair[0]) > remaining(pair[1]) ? pair : [pair[1], pair[0]];
    return [rowSides(larger), rowSides(smaller)];
}

/**
 * Whether `second` makes a multiply-first system with `first` (ADR 0139 §2): on
 * the chosen variable one coefficient is 2 or 3 times the other, and no
 * coefficient is equal or opposite across the pair. With negatives off the
 * equation to multiply is the one with the smaller coefficient there, and it
 * must then have the larger remaining coefficient, so the subtraction after
 * multiplying stays positive (§4).
 */
function multipliesFirst(first: Row, second: Row, onX: boolean, allowNegatives: boolean): boolean {
    const [one, two, otherOne, otherTwo] = onX
        ? [first.a, second.a, first.b, second.b]
        : [first.b, second.b, first.a, second.a];
    const small = Math.min(Math.abs(one), Math.abs(two));
    const big = Math.max(Math.abs(one), Math.abs(two));
    if (big % small !== 0 || (big / small !== 2 && big / small !== 3)) return false;
    if (Math.abs(otherOne) === Math.abs(otherTwo) || determinant(first, second) === 0) return false;
    if (allowNegatives) return true;
    const [multipliedRemaining, otherRemaining] = Math.abs(one) < Math.abs(two) ? [otherOne, otherTwo] : [otherTwo, otherOne];
    return (big / small) * multipliedRemaining > otherRemaining;
}

function multiplyFirstEquations(x: number, y: number, bound: number, allowNegatives: boolean): Equations | null {
    const onX = coin();
    const rows = systemRows(x, y, bound, allowNegatives);
    const pair = drawPair(rows, (first) => rows.filter((second) => multipliesFirst(first, second, onX, allowNegatives)));
    return pair === null ? null : printed(pair);
}

const SYSTEM_BUILDERS: Record<
    SystemKind,
    (x: number, y: number, bound: number, allowNegatives: boolean) => Equations | null
> = {
    substitution: substitutionEquations,
    elimination: eliminationEquations,
    multiplyFirst: multiplyFirstEquations,
};

/**
 * One system of two equations, drawn answer-first like the other solving types
 * (ADR 0139 §3). The solution comes first, each coordinate 1 to the dial and
 * negative on its own coin when negatives are on, then the first equation among
 * those whose constants fit, then the second among those the kind allows with
 * it. A solution that leaves no allowed pair is drawn again.
 *
 * The loop is not capped: a draw succeeds with a probability a test holds well
 * above zero at every dial, and under `withSeed` it ends at the same place every
 * time. The variables are always `x` and `y`, never `randomVariable()`, since
 * the answer is an ordered pair.
 */
export function buildSystemProblem(
    kind: SystemKind,
    maxValue: number,
    key: number,
    language: PrintedLanguage,
    allowNegatives: boolean
): ProblemData {
    const max = usableMax(maxValue);
    // The bound 0138 measured as fitting the width: never three digits.
    const bound = Math.min(2 * max, 99);
    const separator = language === "fr" ? " ; " : ", ";

    for (;;) {
        const x = maybeSigned(getRandomInt(1, max), allowNegatives);
        const y = maybeSigned(getRandomInt(1, max), allowNegatives);
        const equations = SYSTEM_BUILDERS[kind](x, y, bound, allowNegatives);
        if (equations === null) continue;
        return {
            key,
            numbers: [],
            operator: "",
            display: [
                equations.map(([left, right]) => `${left} = ${right}`).join("; "),
                `(${x}${separator}${y})`,
            ],
            algebraQuestion: AlgebraQuestionOptions.SystemsOfEquations,
            system: { equations, x, y, kind, separator },
        };
    }
}

/**
 * The letters a Writing Expressions phrase may use: not "a" ("a number a" reads
 * badly, and French "a" is a verb) and not "y" (Spanish "y" means "and", so
 * "un número y y 8").
 */
export const WRITING_VARIABLES = ALGEBRA_VARIABLES.filter((letter) => letter !== "a" && letter !== "y");

/** The twelve shapes of Writing Expressions, in ADR 0123 §2's order. */
const WRITING_SHAPES = Object.keys(EXPRESSION_SHAPES) as ExpressionShape[];

/** The key for a shape: its own form, whatever the phrasing (ADR 0123 §2). */
function expressionKey(shape: ExpressionShape, v: string, a: number, b: number): string {
    switch (shape) {
        case "S1": return `${v} + ${b}`;
        case "S2": return `${v} - ${b}`;
        case "S3": return `${b} - ${v}`;
        case "S4": return `${a}${v}`;
        case "S5": return `${v} ÷ ${a}`;
        case "S6": return `${b} ÷ ${v}`;
        case "T1": return `${a}${v} + ${b}`;
        case "T2": return `${a}${v} - ${b}`;
        case "T3": return `${b} - ${a}${v}`;
        case "T4": return `${a}(${v} + ${b})`;
        case "T5": return `${a}(${v} - ${b})`;
        default: return `${v} ÷ ${a} + ${b}`;
    }
}

/**
 * One Writing Expressions problem (ADR 0123): a phrase from the language's bank
 * and the expression it stands for.
 *
 * `a` is a multiplier or divisor, 2 to 9; `b` is 2 to the dial. Neither is ever
 * 1 or 0, and nothing is negative, so the dial is the only difficulty setting
 * and nothing here reads the negatives setting. A shape draws only the numbers
 * it uses. Within a shape the phrasing is uniform over those that fit: the
 * "twice" wordings only when `a` is 2, and then alongside the others.
 */
export function buildWritingExpressionProblem(
    shape: ExpressionShape,
    maxValue: number,
    key: number,
    language: PrintedLanguage
): ProblemData {
    const max = usableMax(maxValue);
    const variable = WRITING_VARIABLES[getRandomInt(0, WRITING_VARIABLES.length - 1)];
    const uses = EXPRESSION_SHAPES[shape];
    const a = uses.includes("a") ? getRandomInt(2, Math.min(9, max)) : 0;
    const b = uses.includes("b") ? getRandomInt(2, max) : 0;

    const bank = PROMPTS[language][shape];
    const fitting = bank.map((_, index) => index).filter((index) => !bank[index].twice || a === 2);
    const phrasing = fitting[getRandomInt(0, fitting.length - 1)];
    const values: Record<string, string> = { v: variable, a: String(a), b: String(b) };
    const phrase = bank[phrasing].text.replace(/\{([vab])\}/g, (_, name: string) => values[name]);

    return {
        key,
        numbers: [],
        operator: "",
        display: [phrase, expressionKey(shape, variable, a, b)],
        algebraQuestion: AlgebraQuestionOptions.WritingExpressions,
        expressionWords: { shape, phrasing, a, b },
    };
}

/**
 * One algebra problem. Always succeeds - see the note at the top of the file.
 *
 * `display` is `[prompt, answer]`; `algebraQuestion` tells the renderer which
 * shape the prompt is, so it does not have to infer it from the string. Per the
 * plans README, per-format data goes in its own field and never `numbers[3]`.
 */
export function buildAlgebraProblem(
    question: AlgebraQuestionOptions,
    operator: AlgebraOp,
    maxValue: number,
    key: number,
    allowNegatives = false
): ProblemData {
    if (question === AlgebraQuestionOptions.Equivalent) {
        // Equivalent is dealt by `generateAlgebraPages`; called directly it
        // still has to name a concrete type for the renderer to read.
        question = EXPRESSION_TYPES[getRandomInt(0, EXPRESSION_TYPES.length - 1)];
    }
    if (question === AlgebraQuestionOptions.WritingExpressions) {
        // Dealt by `generateAlgebraPages`, which knows the language; called
        // directly it still has to name a shape, and prints English.
        return buildWritingExpressionProblem(
            WRITING_SHAPES[getRandomInt(0, WRITING_SHAPES.length - 1)],
            maxValue,
            key,
            "en"
        );
    }
    if (question === AlgebraQuestionOptions.SystemsOfEquations) {
        // Dealt by `generateAlgebraPages`, which knows the language; called
        // directly it still has to name a kind, and prints English.
        return buildSystemProblem(
            SYSTEM_KINDS[getRandomInt(0, SYSTEM_KINDS.length - 1)],
            maxValue,
            key,
            "en",
            allowNegatives
        );
    }
    const variable = randomVariable();
    let built: { prompt: string; answer: string };

    switch (question) {
        case AlgebraQuestionOptions.EvaluateExpression:
            built = buildEvaluateProblem(operator, maxValue, variable);
            break;
        case AlgebraQuestionOptions.SolveTwoStep:
            built = buildTwoStepProblem(maxValue, variable, allowNegatives);
            break;
        case AlgebraQuestionOptions.VariablesOnBothSides:
            built = buildBothSidesProblem(maxValue, variable, allowNegatives);
            break;
        case AlgebraQuestionOptions.Inequality:
            built = buildInequalityProblem(maxValue, variable, allowNegatives);
            break;
        case AlgebraQuestionOptions.Distribute:
            built = buildDistributeProblem(maxValue, variable, allowNegatives);
            break;
        case AlgebraQuestionOptions.CombineLikeTerms:
            built = buildCombineProblem(maxValue, variable, allowNegatives);
            break;
        case AlgebraQuestionOptions.Factor:
            built = buildFactorProblem(maxValue, variable);
            break;
        default:
            built = buildSolveProblem(operator, maxValue, variable, allowNegatives);
            break;
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [built.prompt, built.answer],
        algebraQuestion: question,
    };
}

/**
 * What counts as the same problem twice.
 *
 * The variable letter is not part of it: `6y = 18` and `6b = 18` are one
 * equation wearing two letters, and printing both - as an early sheet did,
 * next to each other - reads as a mistake rather than as practice.
 */
function dedupeKey(problem: ProblemData): string {
    return problem.display![0].replace(/[a-z]/g, "·");
}

/**
 * The fingerprint of a Writing Expressions problem: the shape, the phrasing and
 * the numbers (ADR 0123 §4). Not `dedupeKey`, which masks every lower-case
 * letter and so sees a worded prompt as dots, and not the letter, since changing
 * only the letter is the same question.
 */
export function writingExpressionFingerprint(problem: ProblemData): string {
    const { shape, phrasing, a, b } = problem.expressionWords!;
    return `${shape}|${phrasing}|${a}|${b}`;
}

/** A page-shaped set of algebra problems. */
export function generateAlgebraPages(settings: ISettingsProps): ProblemData[][] | null {
    const maxValue = settings.AlgebraMaxValue ?? 12;
    if (settings.AlgebraQuestion === AlgebraQuestionOptions.WritingExpressions) {
        // The only type that reads the language. Each printed page deals the
        // twelve shapes evenly (ADR 0123 §2).
        const language = printedLanguageOf(settings);
        return generateProblemPages({
            settings,
            choices: WRITING_SHAPES,
            build: (shape, key) => buildWritingExpressionProblem(shape, maxValue, key, language),
            fingerprint: writingExpressionFingerprint,
            dealPerPage: true,
        });
    }
    if (settings.AlgebraQuestion === AlgebraQuestionOptions.SystemsOfEquations) {
        // Reads the language for the pair's separator (ADR 0139 §5). Each
        // printed page deals the three kinds evenly (§2); one dimension needs no
        // Latin square, so this is `dealPerPage`, as Writing Expressions is dealt.
        const language = printedLanguageOf(settings);
        const allowNegatives = settings.AllowNegatives === true;
        return generateProblemPages({
            settings,
            choices: SYSTEM_KINDS,
            build: (kind, key) => buildSystemProblem(kind, maxValue, key, language, allowNegatives),
            // The one-line form as printed: x and y are fixed letters, so unlike
            // `dedupeKey` there is nothing to mask.
            fingerprint: (problem) => problem.display![0],
            dealPerPage: true,
        });
    }
    const types = questionTypes(settings.AlgebraQuestion ?? AlgebraQuestionOptions.Mixed);
    const operators = operatorsFor(settings);
    const allowNegatives = settings.AllowNegatives === true;
    return generateProblemPages({
        settings,
        choices: types,
        // The operator is drawn independently of the fair sequence above -
        // it is not itself the thing a Mixed sheet has to cover evenly, the
        // question type is. See `operatorsFor`.
        build: (question, key) => {
            const operator = operators[getRandomInt(0, operators.length - 1)];
            return buildAlgebraProblem(question, operator, maxValue, key, allowNegatives);
        },
        fingerprint: dedupeKey,
        // Equivalent hands each printed page its own even share of the three
        // (ADR 0119 §1). Only that type: the others' sequences stay as they were.
        dealPerPage: settings.AlgebraQuestion === AlgebraQuestionOptions.Equivalent,
    });
}
